import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  FileText,
  Newspaper,
  Pencil,
  Plus,
  Rss,
  ToggleRight,
  Trash2,
  TriangleAlert,
} from "lucide-mui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";
import {
  api,
  getErrorMessage,
  type NewsFeed,
  type NewsFeedCreate,
  type NewsFeedKind,
  type NewsFeedPreview,
} from "../../../api/client.ts";
import { formatDate, formatNumber } from "../../../utils/format.ts";
import { keys } from "../../../api/queryKeys.ts";
import FormattedDate from "../../../components/FormattedDate";
import KpiTile from "../../../components/KpiTile.tsx";
import RelativeTime from "../../../components/RelativeTime.tsx";
import RowActions from "../../../components/RowActions.tsx";
import { StatusLabel } from "../../../components/StatusIcon.tsx";
import EmptyTableRow from "../../../components/table/EmptyTableRow.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import TableCard from "../../../components/table/TableCard.tsx";
import PageHeader from "../../../components/PageHeader.tsx";

const KIND_LABEL: Record<NewsFeedKind, string> = {
  rss: "RSS",
  news_sitemap: "News sitemap",
  sitemap: "Sitemap",
};

const PREVIEW_LIMIT = 25;

function Health({ feed }: { feed: NewsFeed }) {
  if (!feed.last_fetched_at) {
    return <StatusLabel kind="off" label="Never fetched" />;
  }
  if (feed.consecutive_failures > 0) {
    return (
      <Tooltip title={feed.last_error ?? "Unknown error"}>
        <span>
          <StatusLabel kind="error" label={`Failing ${feed.consecutive_failures}×`} />
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={`Last polled ${formatDate(feed.last_fetched_at, "full")}`}>
      <span>
        <StatusLabel kind="ok" label="OK" />
      </span>
    </Tooltip>
  );
}

function ExistingSourceChip({ source }: { source: string | null }) {
  if (source === "apitube") return <Chip size="small" label="APITube" color="warning" variant="outlined" />;
  if (source) return <Chip size="small" label="Stored" variant="outlined" />;
  return <Chip size="small" label="New" color="success" variant="outlined" />;
}

interface FormState {
  name: string;
  url: string;
  kind: NewsFeedKind;
  source_domain: string;
  source_country: string;
  language: string;
  poll_interval_minutes: string;
  fetch_content: boolean;
}

function initialForm(feed: NewsFeed | null): FormState {
  return {
    name: feed?.name ?? "",
    url: feed?.url ?? "",
    kind: feed?.kind ?? "rss",
    source_domain: feed?.source_domain ?? "",
    source_country: feed ? (feed.source_country ?? "") : "NO",
    language: feed ? (feed.language ?? "") : "no",
    poll_interval_minutes: String(feed?.poll_interval_minutes ?? 15),
    fetch_content: feed?.fetch_content ?? true,
  };
}

function PreviewList({ preview }: { preview: NewsFeedPreview }) {
  const shown = preview.items.slice(0, PREVIEW_LIMIT);
  const stored = preview.items.filter((i) => i.existing_source).length;
  const withBody = preview.items.filter((i) => i.has_body).length;
  return (
    <Box>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
        {formatNumber(preview.item_count)} items in the feed
        {preview.items.length > 0 &&
          ` · ${preview.items.length - stored} new · ${stored} already stored · ${withBody} with body in the feed`}
        {preview.items.length > PREVIEW_LIMIT && ` · showing first ${PREVIEW_LIMIT}`}
      </Typography>
      {shown.length === 0 ? (
        <Alert severity="warning">The feed parsed but contained no items.</Alert>
      ) : (
        <Paper variant="outlined" sx={{ maxHeight: 320, overflow: "auto" }}>
          <List dense disablePadding>
            {shown.map((item) => (
              <ListItem
                key={item.url}
                divider
                secondaryAction={<ExistingSourceChip source={item.existing_source} />}
                sx={{ pr: 12 }}
              >
                <ListItemText
                  primary={
                    <Link href={item.url} target="_blank" rel="noopener noreferrer" underline="hover">
                      {item.title || item.url}
                    </Link>
                  }
                  secondary={
                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                      {item.published_at ? (
                        <FormattedDate value={item.published_at} format="datetime" />
                      ) : (
                        "No date"
                      )}
                      {item.has_body && (
                        <Tooltip title="The feed carries the article body">
                          <FileText sx={{ fontSize: 14 }} />
                        </Tooltip>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
}

function FeedDialog({ feed, onClose }: { feed: NewsFeed | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(() => initialForm(feed));
  const [preview, setPreview] = useState<NewsFeedPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));
  const setSource = <K extends "url" | "kind">(key: K, value: FormState[K]) => {
    set(key, value);
    setPreview(null);
    setPreviewError(null);
  };

  const interval = Number(form.poll_interval_minutes);
  const intervalValid = Number.isInteger(interval) && interval >= 5 && interval <= 1440;
  const countryValid = form.source_country.trim() === "" || form.source_country.trim().length === 2;
  const valid =
    form.name.trim() !== "" && form.url.trim().length >= 8 && intervalValid && countryValid;

  const save = useMutation({
    mutationFn: () => {
      const body: NewsFeedCreate = {
        name: form.name.trim(),
        url: form.url.trim(),
        kind: form.kind,
        // Blank: the backend derives it from the URL (create) or keeps it (edit).
        source_domain: form.source_domain.trim() || null,
        source_country: form.source_country.trim().toUpperCase() || null,
        language: form.language.trim() || null,
        poll_interval_minutes: interval,
        fetch_content: form.fetch_content,
      };
      return feed ? api.updateNewsFeed(feed.id, body) : api.createNewsFeed(body);
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: keys.newsFeed.all });
      toast.success(`${saved.name} ${feed ? "saved" : "added"}`);
      onClose();
    },
  });

  // A bad URL shows inline only.
  const runPreview = useMutation({
    mutationFn: () => api.previewNewsFeed({ url: form.url.trim(), kind: form.kind }),
    meta: { suppressErrorToast: true },
    onMutate: () => setPreviewError(null),
    onSuccess: setPreview,
    onError: (err) => {
      setPreview(null);
      setPreviewError(getErrorMessage(err));
    },
  });

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{feed ? `Edit feed: ${feed.name}` : "Add news feed"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            required
            fullWidth
            size="small"
            autoFocus={!feed}
          />
          <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
            <TextField
              label="URL"
              value={form.url}
              onChange={(e) => setSource("url", e.target.value)}
              required
              fullWidth
              size="small"
              helperText="May contain {year} and {month} for monthly sitemaps, {date} for daily ones"
            />
            <TextField
              select
              label="Kind"
              value={form.kind}
              onChange={(e) => setSource("kind", e.target.value as NewsFeedKind)}
              size="small"
              sx={{ minWidth: 160 }}
            >
              {(Object.keys(KIND_LABEL) as NewsFeedKind[]).map((k) => (
                <MenuItem key={k} value={k}>
                  {KIND_LABEL[k]}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
            <TextField
              label="Source domain"
              value={form.source_domain}
              onChange={(e) => set("source_domain", e.target.value)}
              size="small"
              fullWidth
              placeholder="e.g. e24.no"
              helperText={feed ? "Blank keeps the current domain" : "Blank: the URL's host without www"}
            />
            <TextField
              label="Country"
              value={form.source_country}
              onChange={(e) => set("source_country", e.target.value)}
              size="small"
              sx={{ width: 110 }}
              error={!countryValid}
              helperText="ISO-2"
              slotProps={{ htmlInput: { maxLength: 2 } }}
            />
            <TextField
              label="Language"
              value={form.language}
              onChange={(e) => set("language", e.target.value)}
              size="small"
              sx={{ width: 110 }}
              helperText=" "
            />
            <TextField
              label="Poll every (min)"
              type="number"
              value={form.poll_interval_minutes}
              onChange={(e) => set("poll_interval_minutes", e.target.value)}
              size="small"
              sx={{ width: 150 }}
              error={!intervalValid}
              helperText="5–1440"
              slotProps={{ htmlInput: { min: 5, max: 1440, step: 1 } }}
            />
          </Stack>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.fetch_content}
                onChange={(e) => set("fetch_content", e.target.checked)}
              />
            }
            label="Fetch each article page for metadata and body text"
          />
          <Box>
            <Button
              variant="outlined"
              onClick={() => runPreview.mutate()}
              loading={runPreview.isPending}
              loadingPosition="start"
              disabled={form.url.trim().length < 8}
            >
              Preview
            </Button>
          </Box>
          {previewError && <Alert severity="error">{previewError}</Alert>}
          {preview && <PreviewList preview={preview} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => save.mutate()} loading={save.isPending} disabled={!valid}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function bodyShare(feed: NewsFeed): string {
  if (!feed.stats.new_7d) return "—";
  return `${Math.round((feed.stats.with_body_7d / feed.stats.new_7d) * 100)}%`;
}

function LastPoll({ feed }: { feed: NewsFeed }) {
  if (feed.last_item_count == null) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        —
      </Typography>
    );
  }
  return (
    <Tooltip
      title={`Items in the feed / newly stored / already stored by APITube${
        feed.last_fetched_at ? ` · polled ${formatDate(feed.last_fetched_at, "full")}` : ""
      }`}
    >
      <Typography variant="body2" component="span" sx={{ whiteSpace: "nowrap" }}>
        {feed.last_item_count} / {feed.last_new_count ?? 0} / {feed.last_duplicate_count ?? 0}
      </Typography>
    </Tooltip>
  );
}

/**
 * Developer setup of RSS / sitemap news feeds polled alongside APITube, with
 * per-feed health and what each one added over the last week.
 */
export default function NewsFeedsPage() {
  const queryClient = useQueryClient();
  // undefined: closed, null: adding, NewsFeed: editing.
  const [editing, setEditing] = useState<NewsFeed | null | undefined>(undefined);

  const feedsQ = useQuery({
    queryKey: keys.newsFeed.list(),
    queryFn: () => api.getNewsFeeds(),
    refetchInterval: 60_000,
  });
  const feeds = feedsQ.data ?? [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: keys.newsFeed.all });

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.updateNewsFeed(id, { enabled }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (feed: NewsFeed) => api.deleteNewsFeed(feed.id),
    onSuccess: (_, feed) => {
      invalidate();
      toast.success(`${feed.name} deleted`);
    },
  });

  const enabledCount = feeds.filter((f) => f.enabled).length;
  const failingCount = feeds.filter((f) => f.consecutive_failures > 0).length;
  const new24h = feeds.reduce((sum, f) => sum + f.stats.new_24h, 0);

  return (
    <Box>
      <PageHeader
        title="News feeds"
        description={
          <>
            RSS and sitemap feeds polled directly from outlets, alongside APITube. Articles APITube
            already has count as duplicates; deleting a feed keeps its articles.
          </>
        }
        actions={
          <Button variant="contained" startIcon={<Plus />} onClick={() => setEditing(null)}>
            Add feed
          </Button>
        }
      />

      {feedsQ.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getErrorMessage(feedsQ.error)}
        </Alert>
      )}

      {feeds.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 6, md: 3 }}>
            <KpiTile dense label="Feeds" value={feeds.length} icon={<Rss />} />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <KpiTile dense label="Enabled" value={enabledCount} icon={<ToggleRight />} color="success" />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <KpiTile
              dense
              label="Failing"
              value={failingCount}
              icon={<TriangleAlert />}
              color={failingCount ? "error" : "success"}
              emphasis={failingCount > 0}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <KpiTile dense label="New articles 24h" value={new24h} icon={<Newspaper />} />
          </Grid>
        </Grid>
      )}

      <TableCard loading={feedsQ.isLoading}>
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Feed</TableCell>
              <TableCell>Kind</TableCell>
              <TableCell>Domain</TableCell>
              <TableCell>Enabled</TableCell>
              <TableCell>Health</TableCell>
              <TableCell>Last success</TableCell>
              <TableCell>
                <Tooltip title="Items / new / duplicates of APITube on the last poll">
                  <span>Last poll</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">New 24h</TableCell>
              <TableCell align="right">New 7d</TableCell>
              <TableCell align="right">
                <Tooltip title="Articles found in the last 7 days that APITube already had">
                  <span>Dup 7d</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Share of the last 7 days' new articles with body text">
                  <span>Body</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Articles still waiting for their page to be fetched">
                  <span>Awaiting</span>
                </Tooltip>
              </TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {feeds.map((feed) => (
              <TableRow key={feed.id} hover sx={{ opacity: feed.enabled ? 1 : 0.6 }}>
                <TableCell sx={{ maxWidth: 280 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {feed.name}
                  </Typography>
                  <Tooltip title={feed.url}>
                    <Link
                      href={feed.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="caption"
                      underline="hover"
                      sx={{
                        display: "block",
                        color: "text.secondary",
                        fontFamily: "monospace",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {feed.url}
                    </Link>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Chip size="small" label={KIND_LABEL[feed.kind] ?? feed.kind} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{feed.source_domain}</Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {[feed.source_country, feed.language, `every ${feed.poll_interval_minutes}m`]
                      .filter(Boolean)
                      .join(" · ")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Switch
                    size="small"
                    checked={feed.enabled}
                    disabled={toggle.isPending && toggle.variables?.id === feed.id}
                    onChange={(e) => toggle.mutate({ id: feed.id, enabled: e.target.checked })}
                  />
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Health feed={feed} />
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <RelativeTime value={feed.last_success_at} />
                </TableCell>
                <TableCell>
                  <LastPoll feed={feed} />
                </TableCell>
                <TableCell align="right">{formatNumber(feed.stats.new_24h)}</TableCell>
                <TableCell align="right">{formatNumber(feed.stats.new_7d)}</TableCell>
                <TableCell align="right">{formatNumber(feed.stats.duplicates_7d)}</TableCell>
                <TableCell align="right">
                  <Tooltip
                    title={
                      `${formatNumber(feed.stats.with_body_7d)} of ${formatNumber(feed.stats.new_7d)} ` +
                      `with body text${feed.fetch_content ? "" : " · page fetching is off"}`
                    }
                  >
                    <span>{bodyShare(feed)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">{formatNumber(feed.stats.awaiting_content)}</TableCell>
                <TableCell padding="checkbox" align="right">
                  <RowActions
                    label={feed.name}
                    actions={[
                      { label: "Edit", icon: <Pencil fontSize="small" />, onClick: () => setEditing(feed) },
                      {
                        label: "Delete",
                        icon: <Trash2 fontSize="small" />,
                        destructive: true,
                        onClick: () => remove.mutateAsync(feed),
                        confirm: {
                          title: `Delete feed: ${feed.name}`,
                          body: "Stop polling it and remove it? Articles it already stored are kept.",
                        },
                      },
                    ]}
                  />
                </TableCell>
              </TableRow>
            ))}
            {feeds.length === 0 && <EmptyTableRow colSpan={13}>No news feeds yet</EmptyTableRow>}
          </TableBody>
        </ScrollTable>
      </TableCard>

      {editing !== undefined && (
        <FeedDialog key={editing?.id ?? "new"} feed={editing} onClose={() => setEditing(undefined)} />
      )}
    </Box>
  );
}
